import { allPrompts, composePromptPlan } from "../data/prompts";
import { interiorModules, type DownloadAsset } from "../data/catalog";
import {
  aPlusStoryboards,
  coverFamilySystems,
  crossSellTemplates,
  interiorLayoutMasters,
  leadMagnetSystems,
  resolveCoverPalette,
  welcomeSequences,
} from "../data/expansion";
import type { OfferSku, PaperKingdomProject } from "../domain/schema";
import {
  getChannelAccountReady,
  getChannelDelivery,
  getChannelListing,
  getMarketplaceGuide,
  getSellingKitGuide,
} from "../data/marketplaces";
import { getProjectReadinessSummary } from "./projectProgress";
import {
  getPromptIdFromWorkKey,
  getPromptWorkEntriesForChannel,
} from "./promptWork";

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

function safeFilename(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function downloadProjectJson(project: PaperKingdomProject) {
  downloadText(
    `${safeFilename(project.name) || "paper-kingdom-project"}.json`,
    JSON.stringify(project, null, 2),
    "application/json;charset=utf-8",
  );
}

export function downloadPromptPlan(project: PaperKingdomProject) {
  downloadText(
    `${safeFilename(project.name) || "paper-kingdom"}-prompts.md`,
    composePromptPlan(project),
    "text/markdown;charset=utf-8",
  );
}

export function composeProductionDossier(project: PaperKingdomProject): string {
  const marketplaceGuide = getMarketplaceGuide(project.marketplace);
  const coverSystem = coverFamilySystems.find(
    (system) => system.id === project.production.coverSystemId,
  );
  const coverPalette = resolveCoverPalette(
    project.production.coverPaletteId,
    project.production.customCoverPalette,
  );
  const itemNoun = marketplaceGuide.usesKdpPrintSetup ? "book" : "product";
  const interiorPlans = project.catalogue
    .map((book) => {
      const plan = project.production.interiorPlans[book.id];
      const layoutNames = (plan?.layoutIds ?? [])
        .map((id) => interiorLayoutMasters.find((layout) => layout.id === id)?.name ?? id)
        .join(", ");
      const moduleNames = (plan?.moduleIds ?? [])
        .map((id) => interiorModules.find((module) => module.id === id)?.name ?? id)
        .join(", ");
      return `### ${book.sequence}. ${book.title || `Untitled ${itemNoun}`}
- Layout masters: ${layoutNames || "[not selected]"}
- Reusable page types: ${moduleNames || "[not selected]"}
- Sequence notes: ${plan?.sequenceNotes || "[not recorded]"}`;
    })
    .join("\n\n");
  const coverPlans = project.catalogue
    .map((book) => {
      const plan = project.production.coverPlans[book.id];
      return `### ${book.sequence}. ${book.title || `Untitled ${itemNoun}`}
- ${marketplaceGuide.usesKdpPrintSetup ? "Front-cover" : "Preview"} title: ${book.title || "[not written]"}
- Subtitle: ${book.subtitle || "[none]"}
- ${marketplaceGuide.usesKdpPrintSetup ? "Back-cover message" : "Preview message"}: ${plan?.backCoverCopy || "[not written]"}`;
    })
    .join("\n\n");
  const rights = project.production.rightsLedger.length
    ? project.production.rightsLedger
        .map(
          (item) =>
            `- ${item.asset} — ${item.status}; source: ${item.source || "[not recorded]"}; licence: ${item.licence || "[not recorded]"}; notes: ${item.notes || "none"}`,
        )
        .join("\n")
    : "- No rights items recorded.";
  const proofs = project.production.proofLog.length
    ? project.production.proofLog
        .map((item) => {
          const book = project.catalogue.find((candidate) => candidate.id === item.bookId);
          return `- ${book?.title || `Untitled ${itemNoun}`} — ${item.outcome}; reviewed ${item.reviewedAt || "[date not recorded]"}; ${item.notes || "no notes"}`;
        })
        .join("\n")
    : `- No ${marketplaceGuide.usesKdpPrintSetup ? "printed-proof" : "test-purchase or file-review"} records recorded.`;
  const preflight = Object.entries(project.production.preflight)
    .map(([key, done]) => `- [${done ? "x" : " "}] ${key}`)
    .join("\n");

  const productionSpecifications = marketplaceGuide.usesKdpPrintSetup
    ? `- Finished book size: ${project.production.trimSize}
- Paper: ${project.production.paperType}
- Artwork reaches the page edge: ${project.production.bleed === "Bleed" ? "Yes" : "No"}
- Planned interior pages: ${project.production.plannedPageCount}
- Inside PDF created: ${project.production.primaryFileCreated ? "Yes" : "No"}
- Cover preview created: ${project.production.coverPdfCreated ? "Yes" : "No"}
- Barcode space: ${project.production.barcodeTreatment}`
    : `- Sales channel: ${marketplaceGuide.shortName}
- Customer delivery or fulfilment: ${getChannelDelivery(project) || "[not recorded]"}
- KDP-only spine and barcode fields: not applicable`;

  return `# ${project.name} — Production Notes

Generated by Paper Kingdom ${project.productVersion}
Project revision: ${project.revision}
Generated: ${new Date().toISOString()}

## ${marketplaceGuide.usesKdpPrintSetup ? "Cover" : "Listing-preview"} plan

- Style: ${coverSystem?.name || "[not selected]"}
- Product palette: ${coverPalette?.name || "[not selected]"}
- Product colours: ${coverPalette?.colors.map((color) => `${color.name} (${color.hex})`).join("; ") || "[not recorded]"}
- Colour note: These colours belong to the product, not the Paper Kingdom interface.
- Extra instruction: ${project.production.coverNotes || "[none]"}

### Text to add and buyer message

${coverPlans}

## Interior plans

${interiorPlans}

## Customer files

${productionSpecifications}
- Typography: ${project.production.typographySpecification || "[not recorded]"}
${marketplaceGuide.usesKdpPrintSetup
  ? `- File review:
${Object.entries(project.production.customerFileChecks)
  .map(([check, done]) => `  - [${done ? "x" : " "}] ${check}`)
  .join("\n")}`
  : ""}

## Rights and asset ledger

${rights}

## ${marketplaceGuide.usesKdpPrintSetup ? "Printed-proof review" : "Test-purchase or file review"}

${proofs}

## Preflight

${preflight || "- No preflight decisions recorded."}

## Revision notes

${project.production.revisionNotes || "[none]"}

> Verify current marketplace specifications, rights, disclosure requirements, and the final customer experience before publishing.`;
}

export function downloadProductionDossier(project: PaperKingdomProject) {
  downloadText(
    `${safeFilename(project.name) || "paper-kingdom"}-production-dossier.md`,
    composeProductionDossier(project),
    "text/markdown;charset=utf-8",
  );
}

export function composeGrowthPackage(project: PaperKingdomProject): string {
  const marketplaceGuide = getMarketplaceGuide(project.marketplace);
  const sellingGuide = getSellingKitGuide(project.marketplace);
  const itemNoun = marketplaceGuide.usesKdpPrintSetup ? "book" : "product";
  const listings = project.catalogue
    .map((book) => {
      const listing = getChannelListing(project, book.id);
      const listingTitle = sellingGuide.titleEditable
        ? listing?.listingTitle || book.title || `Untitled ${itemNoun}`
        : book.title || `Untitled ${itemNoun}`;
      const seo = sellingGuide.showSeoFields
        ? `\n- Search-engine title: ${listing?.seoTitle || "[not written]"}\n- Search-engine description: ${listing?.seoDescription || "[not written]"}`
        : "";
      const socialPosts = (listing?.socialPosts ?? [])
        .filter(Boolean)
        .map((post, index) => `${index + 1}. ${post}`)
        .join("\n") || "[not written]";
      const visualPlan = (listing?.visualPlan ?? [])
        .slice(0, sellingGuide.visualCount)
        .filter(Boolean)
        .map((item, index) => `${index + 1}. ${item}`)
        .join("\n") || "[not planned]";
      return `## ${book.sequence}. ${book.title || `Untitled ${itemNoun}`}

- Listing title: ${listingTitle}
- Subtitle: ${book.subtitle || "[not recorded]"}
- Audience detail: ${listing?.audienceDetails || "[not recorded]"}
- Description:

${listing?.description || "[not written]"}

- ${sellingGuide.searchCount} ${sellingGuide.searchLabel}: ${(listing?.keywords ?? []).slice(0, sellingGuide.searchCount).filter(Boolean).join("; ") || "[not recorded]"}${seo}

### Promotion copy

- Short pitch: ${listing?.shortPitch || "[not written]"}
- Launch email subject: ${listing?.launchEmailSubject || "[not written]"}
- Launch email:

${listing?.launchEmailBody || "[not written]"}

#### Social posts

${socialPosts}

### ${sellingGuide.visualLabel}

${visualPlan}`;
    })
    .join("\n\n");
  const aPlus = marketplaceGuide.supportsAPlus && project.launchGrowth.aPlusModules.length
    ? project.launchGrowth.aPlusModules
        .map((module, index) => {
          const storyboard =
            aPlusStoryboards.find((item) => item.id === module.storyboardId)?.name ??
            module.storyboardId;
          return `### Module ${index + 1} — ${storyboard}
- Headline: ${module.headline || "[not written]"}
- Body: ${module.body || "[not written]"}
- Image brief: ${module.imageBrief || "[not written]"}`;
        })
        .join("\n\n")
    : marketplaceGuide.supportsAPlus
      ? "No Amazon extra-content modules planned."
      : "Not applicable to the selected sales channel.";
  const leadMagnet =
    leadMagnetSystems.find((system) => system.id === project.launchGrowth.leadMagnetId) ??
    leadMagnetSystems[0];
  const welcome =
    welcomeSequences.find((sequence) => sequence.id === project.launchGrowth.welcomeSequenceId) ??
    welcomeSequences[0];
  const crossSells = project.launchGrowth.crossSellTemplateIds
    .map((id) => crossSellTemplates.find((template) => template.id === id)?.name ?? id)
    .join(", ");
  const readerFollowUp = project.launchGrowth.funnelEnabled
    ? `- Optional resource: ${leadMagnet.name}
- Promise: ${leadMagnet.promise}
- Structure: ${leadMagnet.structure}
- Destination: ${project.launchGrowth.qrDestination || "[not recorded]"}
- Product insert or invitation: ${project.launchGrowth.insertCopy || "[not written]"}
- Signup headline: ${project.launchGrowth.optInHeadline || "[not written]"}
- Signup explanation: ${project.launchGrowth.optInBody || "[not written]"}
- Welcome sequence: ${welcome.name}
- Email path: ${welcome.emails.join(" → ")}
- Related-product paths: ${crossSells || "[none selected]"}
- Consent and delivery notes: ${project.launchGrowth.funnelNotes || "[none]"}`
    : "Optional reader follow-up is not enabled. No welcome emails or cross-sell work is counted.";
  const publishingSteps = sellingGuide.uploadSteps
    .map((step, index) => `${index + 1}. ${step}`)
    .join("\n");

  return `# ${project.name} — Selling Kit

Generated by Paper Kingdom ${project.productVersion}
Project revision: ${project.revision}
Generated: ${new Date().toISOString()}

# Listing records

${listings}

# ${marketplaceGuide.supportsAPlus ? "Optional Amazon product-page content" : "Channel-specific optional product-page content"}

${aPlus}

# Publishing steps

${publishingSteps}

- ${marketplaceGuide.accountStepLabel}: ${getChannelAccountReady(project) ? "Yes" : "No"}

# Optional reader follow-up

${readerFollowUp}

> Search phrases require live marketplace verification. Do not infer demand, ranking, sales, advertising performance, or platform approval from this plan.`;
}

export function downloadGrowthPackage(project: PaperKingdomProject) {
  downloadText(
    `${safeFilename(project.name) || "paper-kingdom"}-selling-kit.md`,
    composeGrowthPackage(project),
    "text/markdown;charset=utf-8",
  );
}

export function composeCompleteHandoff(
  project: PaperKingdomProject,
  entitlements: OfferSku[],
): string {
  const readiness = getProjectReadinessSummary(project, entitlements);
  const readinessText = readiness
    .map((section) => {
      const details = section.complete
        ? "- Complete for the information currently required."
        : section.missing.map((item) => `- [ ] ${item}`).join("\n");
      return `### ${section.title}
${details}`;
    })
    .join("\n\n");
  const approvedResults = getPromptWorkEntriesForChannel(project)
    .filter(([, work]) => work.status === "approved" && work.response.trim())
    .map(([key, work]) => {
      const prompt = allPrompts.find(
        (item) => item.id === getPromptIdFromWorkKey(key),
      );
      return `### ${prompt?.title ?? "Reviewed AI result"}

- Approved for later steps: ${work.useInLaterSteps ? "Yes" : "No"}
- Reviewed: ${work.approvedAt || "[date not recorded]"}

${work.response.trim()}`;
    })
    .join("\n\n");

  return `# ${project.name} — Complete Project Handoff

Generated by Paper Kingdom ${project.productVersion}
Project revision: ${project.revision}
Generated: ${new Date().toISOString()}
Sales channel: ${project.marketplace}
Product format: ${project.format}

> This one document gathers the work from every step. It does not turn an unfinished plan into a finished customer file. Complete every unchecked item before publishing.

## Final readiness

${readinessText}

## Reviewed AI results

${approvedResults || "No AI answers have been pasted and approved yet."}

---

${composePromptPlan(project)}

---

${composeProductionDossier(project)}

---

${composeGrowthPackage(project)}
`;
}

export function downloadCompleteHandoff(
  project: PaperKingdomProject,
  entitlements: OfferSku[],
) {
  downloadText(
    `${safeFilename(project.name) || "paper-kingdom"}-complete-handoff.md`,
    composeCompleteHandoff(project, entitlements),
    "text/markdown;charset=utf-8",
  );
}

export function downloadAsset(asset: DownloadAsset) {
  downloadText(`${safeFilename(asset.name)}.md`, asset.content, "text/markdown;charset=utf-8");
}

export async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    try {
      await Promise.race([
        navigator.clipboard.writeText(value),
        new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(new Error("Clipboard request timed out")), 5_000);
        }),
      ]);
      return;
    } catch {
      // Some embedded browsers expose the Clipboard API but never resolve the
      // permission request. Fall through to the local document copy path.
    }
  }

  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.inset = "0 auto auto -9999px";
  document.body.appendChild(textArea);
  textArea.select();

  const copied = document.execCommand("copy");
  textArea.remove();
  if (!copied) throw new Error("Clipboard copy was not available");
}
